/**
 * Self-test suite: SW E2EE primitive re-use (no vendored fork).
 *
 * Build-hygiene companion to `sw-e2ee-resolution.test.ts`. The vitest file
 * exercises the actual SW module load + filesystem scan; this browser-runnable
 * suite exercises the PURE FUNCTION at the heart of the fork-detection logic
 * (`findVendoredCryptoForks`) against in-memory fixture paths.
 *
 * The split exists because the SelfTestRunner registry is invoked from the
 * browser (`if (typeof import.meta.env?.DEV)` in `selftest/index.ts`); a real
 * `import("../../../src/serviceWorker")` would pull in `/// <reference lib=
 * "webworker" />` types and fail at module-eval inside a normal browser
 * window context. Vitest is the right harness for that. Here we only test
 * the pure logic.
 *
 * Mirrors iOS `feedback_xcode_pbxproj.md` concerns: the NSE / Share targets
 * shipping their own copy of `SignalCrypto.swift` is the canonical footgun.
 * The web equivalent is a `src/sw-crypto/` directory or a sibling vendored
 * envelope.ts under the SW source tree.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";

// ---------------------------------------------------------------------------
// Pure logic — also exported for the vitest file to consume, though that
// file uses real filesystem access for a stronger guarantee.
// ---------------------------------------------------------------------------

/**
 * Heuristic patterns that indicate a vendored copy of `e2ee/crypto/*` under
 * a SW-only path. Matched against POSIX-style relative paths.
 *
 * The legitimate shared tree lives at `packages/client/components/e2ee/crypto/`
 * and `packages/client/components/e2ee/store/`. Anything else that names a
 * crypto/store primitive is a fork.
 *
 * `sw-push/` is INTENTIONALLY not flagged — it is the push delivery
 * pipeline (parser, render policy, dedup guard), not a crypto fork.
 */
const VENDORED_FORK_PATTERNS: readonly RegExp[] = [
  // `src/sw-crypto/anything`
  /(?:^|\/)src\/sw-crypto\//,
  // `src/serviceWorker-crypto/anything`
  /(?:^|\/)src\/serviceWorker-crypto\//,
  // `src/sw/<file>` where the file names a primitive
  /(?:^|\/)src\/sw\/(?:envelope|session|SignalProtocolStore|SignalCrypto|SignalStore)\.ts$/,
  // `sw-push/crypto/anything` — push tree must NEVER hold a crypto subdir
  /(?:^|\/)sw-push\/crypto\//,
  // `sw-push/envelope.ts` / `sw-push/session.ts` etc — flat fork
  /(?:^|\/)sw-push\/(?:envelope|session|SignalProtocolStore|SignalCrypto|SignalStore)\.ts$/,
];

/**
 * Pure function. Given a set of file paths (relative, POSIX-style), return
 * the paths that look like a vendored fork of an `e2ee/crypto/*` primitive
 * under a Service-Worker-only tree.
 *
 * Exported so the vitest file and any future CI lint can re-use it.
 */
export function findVendoredCryptoForks(
  filePaths: readonly string[],
): readonly string[] {
  const offenders: string[] = [];
  for (const p of filePaths) {
    for (const pattern of VENDORED_FORK_PATTERNS) {
      if (pattern.test(p)) {
        offenders.push(p);
        break;
      }
    }
  }
  return offenders;
}

// ---------------------------------------------------------------------------
// Test cases
// ---------------------------------------------------------------------------

/** 1. Clean tree — only legitimate paths — returns empty offenders list. */
function testNoForksInCleanFixture(): TestCase {
  const name = "passes with no forks in fixture";
  const fixture: readonly string[] = [
    "packages/client/components/e2ee/crypto/envelope.ts",
    "packages/client/components/e2ee/crypto/session.ts",
    "packages/client/components/e2ee/store/SignalProtocolStore.ts",
    "packages/client/components/e2ee/sw-push/SWPushPayload.ts",
    "packages/client/components/e2ee/sw-push/SWRenderPolicy.ts",
    "packages/client/components/e2ee/sw-push/SWDuplicateGuard.ts",
    "packages/client/src/serviceWorker.ts",
    "packages/client/src/index.tsx",
  ];
  const offenders = findVendoredCryptoForks(fixture);
  if (offenders.length !== 0) {
    return fail(
      name,
      `expected no offenders, got [${offenders.join(", ")}]`,
    );
  }
  return pass(name);
}

/** 2. Vendored fork under src/sw-crypto/ is flagged. */
function testFlagsVendoredForkUnderSwCrypto(): TestCase {
  const name = "flags a vendored fork under sw-crypto/";
  const fixture: readonly string[] = [
    "packages/client/components/e2ee/crypto/envelope.ts",
    "packages/client/src/sw-crypto/envelope.ts",
    "packages/client/src/sw-crypto/session.ts",
  ];
  const offenders = findVendoredCryptoForks(fixture);
  if (offenders.length !== 2) {
    return fail(name, `expected 2 offenders, got ${offenders.length}`);
  }
  if (
    !offenders.includes("packages/client/src/sw-crypto/envelope.ts") ||
    !offenders.includes("packages/client/src/sw-crypto/session.ts")
  ) {
    return fail(
      name,
      `expected both sw-crypto/ files flagged, got [${offenders.join(", ")}]`,
    );
  }
  return pass(name);
}

/** 3. The legitimate sw-push/ tree is NOT a crypto fork — must not flag it. */
function testDoesNotFlagLegitimateSwPushTree(): TestCase {
  const name =
    "does not flag the legitimate sw-push suite (sw-push is the push pipeline, not a crypto fork)";
  const fixture: readonly string[] = [
    "packages/client/components/e2ee/sw-push/SWPushPayload.ts",
    "packages/client/components/e2ee/sw-push/SWPushSelfTest.ts",
    "packages/client/components/e2ee/sw-push/SWRenderPolicy.ts",
    "packages/client/components/e2ee/sw-push/SWDuplicateGuard.ts",
  ];
  const offenders = findVendoredCryptoForks(fixture);
  if (offenders.length !== 0) {
    return fail(
      name,
      `sw-push/ push pipeline must not be flagged; got [${offenders.join(", ")}]`,
    );
  }
  return pass(name);
}

/** 4. Sibling SW serviceWorker-crypto/ directory is flagged. */
function testFlagsServiceWorkerCryptoSibling(): TestCase {
  const name = "flags serviceWorker-crypto/ sibling";
  const fixture: readonly string[] = [
    "packages/client/src/serviceWorker.ts",
    "packages/client/src/serviceWorker-crypto/envelope.ts",
  ];
  const offenders = findVendoredCryptoForks(fixture);
  if (offenders.length !== 1) {
    return fail(name, `expected 1 offender, got ${offenders.length}`);
  }
  if (offenders[0] !== "packages/client/src/serviceWorker-crypto/envelope.ts") {
    return fail(name, `wrong offender: ${offenders[0]}`);
  }
  return pass(name);
}

/** 5. Flat sw-push/envelope.ts (someone vendoring INTO sw-push) is flagged. */
function testFlagsFlatForkUnderSwPush(): TestCase {
  const name = "flags flat envelope.ts vendored into sw-push/";
  const fixture: readonly string[] = [
    "packages/client/components/e2ee/sw-push/SWPushPayload.ts",
    "packages/client/components/e2ee/sw-push/envelope.ts",
    "packages/client/components/e2ee/sw-push/session.ts",
  ];
  const offenders = findVendoredCryptoForks(fixture);
  if (offenders.length !== 2) {
    return fail(name, `expected 2 offenders, got ${offenders.length}`);
  }
  return pass(name);
}

/** 6. src/sw/envelope.ts (file-level fork inside sw dir) is flagged. */
function testFlagsSwDirPrimitiveFile(): TestCase {
  const name = "flags primitive file inside src/sw/ dir";
  const fixture: readonly string[] = [
    "packages/client/src/sw/envelope.ts",
    "packages/client/src/sw/SignalProtocolStore.ts",
    // Non-primitive file under sw/ should NOT flag.
    "packages/client/src/sw/handlers.ts",
  ];
  const offenders = findVendoredCryptoForks(fixture);
  if (offenders.length !== 2) {
    return fail(
      name,
      `expected 2 offenders (envelope.ts + SignalProtocolStore.ts), ` +
        `got [${offenders.join(", ")}]`,
    );
  }
  if (offenders.includes("packages/client/src/sw/handlers.ts")) {
    return fail(name, "handlers.ts is not a primitive and must not flag");
  }
  return pass(name);
}

/** 7. Empty fixture returns empty list (boundary). */
function testEmptyFixture(): TestCase {
  const name = "empty fixture returns empty offender list";
  const offenders = findVendoredCryptoForks([]);
  if (offenders.length !== 0) {
    return fail(name, `expected empty list, got ${offenders.length}`);
  }
  return pass(name);
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "SW E2EE primitive re-use (no vendored fork)",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      testNoForksInCleanFixture(),
      testFlagsVendoredForkUnderSwCrypto(),
      testDoesNotFlagLegitimateSwPushTree(),
      testFlagsServiceWorkerCryptoSibling(),
      testFlagsFlatForkUnderSwPush(),
      testFlagsSwDirPrimitiveFile(),
      testEmptyFixture(),
    ]),
});
